import { useState, useEffect } from 'react';
import { getTokenFromLocalStorage } from '@/utils/auth0Token';

/**
 * Checks if the user is authenticated by reading directly from localStorage
 * This ensures isAuthenticated can never be out of sync with actual token state
 * 
 * @returns {boolean} True if user is authenticated, false otherwise
 */
const isUserAuthenticated = () => {
	if (typeof window === 'undefined') {
		return false;
	}

	// Only consider the user authenticated when we have a non-expired access token.
	// This prevents stale Auth0 cache entries from causing redirect loops.
	return !!getTokenFromLocalStorage();
};

/**
 * Hook that reads isAuthenticated directly from localStorage
 * This ensures it can never be out of sync with actual token state
 * 
 * @returns {boolean} True if user is authenticated, false otherwise
 */
export const useIsAuthenticated = () => {
	const [isAuthenticated, setIsAuthenticated] = useState(() => isUserAuthenticated());

	useEffect(() => {
		// Check immediately on mount
		setIsAuthenticated(isUserAuthenticated());

		// Listen for storage changes (logout in other tabs, localStorage.clear(), etc.)
		const handleStorageChange = (e) => {
			// Check if Auth0 cache was modified
			if (e.key && e.key.includes('@@auth0spajs@@')) {
				setIsAuthenticated(isUserAuthenticated());
			}
			// Also check if localStorage was cleared (logout)
			if (e.key === null) {
				// localStorage.clear() was called - check all keys
				setIsAuthenticated(isUserAuthenticated());
			}
		};

		window.addEventListener('storage', handleStorageChange);

		// Poll periodically to catch changes that don't trigger storage events
		// (e.g., same-tab localStorage changes)
		const interval = setInterval(() => {
			const currentAuth = isUserAuthenticated();
			if (currentAuth !== isAuthenticated) {
				setIsAuthenticated(currentAuth);
			}
		}, 500); // Check every 500ms

		return () => {
			window.removeEventListener('storage', handleStorageChange);
			clearInterval(interval);
		};
	}, [isAuthenticated]);

	return isAuthenticated;
};
